// =============================================================================
// AI 渗透率行业框架配置 (Top-Down Model by User)
// =============================================================================
export interface IndustryFramework {
  name: string;
  weight: number;
  keywords: string[]; // 用于通用API查询的关键词 (News, GitHub)
  arxivKeywords?: string[]; // 用于arXiv的专用学术关键词
  description: string;
  // 此处为静态估算值，未来将由动态数据计算替代
  staticPenetration: number;
}

export const INDUSTRY_SECTORS: Record<string, IndustryFramework> = {
  // =============================================================================
  // 1. 信息技术与软件 (权重: 0.15)
  // =============================================================================
  infoTech: {
    name: '信息技术与软件',
    weight: 0.15,
    keywords: ['AI-powered software development', 'DevOps', 'AIOps', 'cybersecurity AI', 'copilot', 'code generation', 'cloud computing AI'],
    arxivKeywords: ['program synthesis', 'code generation models', 'automated testing', 'formal verification', 'AI in cybersecurity', 'large language models for code'],
    description: 'AI辅助编程、智能运维、AI网络安全',
    staticPenetration: 0.75
  },

  // =============================================================================
  // 2. 媒体与内容创作 (权重: 0.12)
  // =============================================================================
  media: {
    name: '媒体与内容创作',
    weight: 0.12,
    keywords: ['generative AI', 'AIGC', 'content creation', 'text-to-image', 'video synthesis', 'AI writing', 'virtual production'],
    arxivKeywords: ['generative adversarial networks', 'diffusion models', 'neural rendering', 'style transfer', 'transformer models for text generation'],
    description: 'AI生成文本、图像、视频、音乐',
    staticPenetration: 0.85
  },

  // =============================================================================
  // 3. 金融服务 (权重: 0.10)
  // =============================================================================
  finance: {
    name: '金融服务',
    weight: 0.10,
    keywords: ['FinTech', 'algorithmic trading', 'fraud detection', 'credit scoring', 'robo-advisors', 'quantitative analysis', 'insurtech'],
    arxivKeywords: ['algorithmic trading strategies', 'financial forecasting', 'fraud detection algorithms', 'sentiment analysis for finance', 'blockchain AI'],
    description: '量化交易、智能投顾、反欺诈',
    staticPenetration: 0.60
  },

  // =============================================================================
  // 4. 医疗健康与生命科学 (权重: 0.10)
  // =============================================================================
  healthcare: {
    name: '医疗健康与生命科学',
    weight: 0.10,
    keywords: ['health tech', 'medical diagnostics AI', 'drug discovery', 'computational biology', 'robotic surgery', 'personalized medicine', 'genomic analysis'],
    arxivKeywords: ['medical image analysis', 'computational drug discovery', 'protein folding', 'genomic data analysis', 'clinical trial optimization', 'federated learning for healthcare'],
    description: 'AI辅助诊断、药物研发、机器人手术',
    staticPenetration: 0.40
  },
  
  // =============================================================================
  // 5. 制造与工业 (权重: 0.08)
  // =============================================================================
  manufacturing: {
    name: '制造与工业',
    weight: 0.08,
    keywords: ['smart factory', 'industrial AI', 'predictive maintenance', 'quality control automation', 'robotics', 'digital twin', 'supply chain AI'],
    arxivKeywords: ['predictive maintenance models', 'automated visual inspection', 'reinforcement learning for robotics', 'digital twin simulation', 'supply chain optimization'],
    description: '智能工厂、预测性维护、AI质检',
    staticPenetration: 0.45
  },

  // =============================================================================
  // 6. 零售与电子商务 (权重: 0.08)
  // =============================================================================
  retail: {
    name: '零售与电子商务',
    weight: 0.08,
    keywords: ['recommendation engines', 'supply chain optimization', 'customer analytics', 'personalized marketing', 'inventory management AI', 'e-commerce AI'],
    arxivKeywords: ['recommender systems', 'collaborative filtering', 'customer churn prediction', 'demand forecasting', 'dynamic pricing'],
    description: '智能推荐、供应链优化、用户分析',
    staticPenetration: 0.65
  },

  // =============================================================================
  // 7. 交通与物流 (权重: 0.07)
  // =============================================================================
  transportation: {
    name: '交通与物流',
    weight: 0.07,
    keywords: ['autonomous vehicles', 'route optimization', 'logistics network AI', 'drone delivery', 'intelligent traffic management', 'mobility as a service'],
    arxivKeywords: ['autonomous driving perception', 'path planning algorithms', 'traffic flow prediction', 'swarm intelligence for logistics', 'vehicle-to-everything (V2X) communication'],
    description: '自动驾驶、智能路线规划、无人配送',
    staticPenetration: 0.35
  },

  // =============================================================================
  // 8. 专业服务 (权重: 0.07)
  // =============================================================================
  professionalServices: {
    name: '专业服务',
    weight: 0.07,
    keywords: ['legal tech', 'contract analysis', 'e-discovery', 'AI consulting', 'RPA', 'business process automation', 'audit AI'],
    arxivKeywords: ['natural language processing for legal documents', 'automated reasoning', 'business process mining', 'explainable AI in legal tech'],
    description: '法律合同分析、自动化审计、AI咨询',
    staticPenetration: 0.30
  },
  
  // =============================================================================
  // 9. 教育 (权重: 0.06)
  // =============================================================================
  education: {
    name: '教育',
    weight: 0.06,
    keywords: ['EdTech', 'personalized learning', 'AI tutor', 'automated grading', 'adaptive learning platform'],
    arxivKeywords: ['intelligent tutoring systems', 'knowledge tracing', 'educational data mining', 'automated essay scoring'],
    description: '个性化学习、AI家教、自动评分',
    staticPenetration: 0.50
  },
  
  // =============================================================================
  // 10. 科学研究 (权重: 0.06)
  // =============================================================================
  scientificResearch: {
    name: '科学研究',
    weight: 0.06,
    keywords: ['computational science', 'climate modeling AI', 'materials discovery AI', 'genomic sequencing AI', 'particle physics AI', 'astronomy AI'],
    arxivKeywords: ['AI for scientific discovery', 'physics-informed neural networks', 'generative models for materials', 'climate informatics', 'astroinformatics'],
    description: 'AI加速新材料发现、气候模拟、基因分析',
    staticPenetration: 0.40
  },

  // =============================================================================
  // 11. 能源与公共事业 (权重: 0.06)
  // =============================================================================
  energy: {
    name: '能源与公共事业',
    weight: 0.06,
    keywords: ['smart grid', 'energy forecasting', 'renewable energy optimization', 'utility management AI', 'climate tech'],
    arxivKeywords: ['load forecasting', 'energy system optimization', 'smart grid control', 'AI for renewable energy', 'climate change modeling'],
    description: '智能电网、能源需求预测、气候科技',
    staticPenetration: 0.25
  },

  // =============================================================================
  // 12. 政府与公共部门 (权重: 0.05)
  // =============================================================================
  publicSector: {
    name: '政府与公共部门',
    weight: 0.05,
    keywords: ['govtech', 'smart city', 'public safety AI', 'policy simulation', 'administrative automation'],
    arxivKeywords: ['urban computing', 'public policy analysis', 'computational social science', 'AI for social good', 'smart city infrastructure'],
    description: '智慧城市、公共安全AI、智能政务',
    staticPenetration: 0.15
  }
};

// 数据源配置文件
// 管理所有API端点、密钥和配置参数

export interface DataSourceConfig {
  name: string;
  endpoint: string;
  apiKey?: string;
  rateLimit: number; // 每分钟请求数
  free: boolean; // 是否为免费API
  description: string;
  documentation: string;
}

export const DATA_SOURCES: Record<string, DataSourceConfig> = {
  // =============================================================================
  // 免费数据源 (立即可用)
  // =============================================================================
  github: {
    name: 'GitHub API',
    endpoint: 'https://api.github.com',
    rateLimit: 60, // 未认证：60/小时，认证：5000/小时
    free: true,
    description: '开源AI项目数据、代码库统计、编程语言趋势',
    documentation: 'https://docs.github.com/en/rest'
  },

  arxiv: {
    name: 'arXiv API',
    endpoint: 'https://export.arxiv.org/api',
    rateLimit: 100,
    free: true,
    description: '学术论文数据、AI研究趋势、引用统计',
    documentation: 'https://arxiv.org/help/api'
  },

  worldbank: {
    name: 'World Bank Open Data',
    endpoint: 'https://api.worldbank.org/v2',
    rateLimit: 120,
    free: true,
    description: '各国数字化指标、互联网普及率、科技投入',
    documentation: 'https://datahelpdesk.worldbank.org/knowledgebase/articles/889392'
  },

  reddit: {
    name: 'Reddit API',
    endpoint: 'https://www.reddit.com/api/v1',
    rateLimit: 100,
    free: true,
    description: 'AI讨论热度、社区趋势、技术话题分析',
    documentation: 'https://www.reddit.com/dev/api'
  },

  hackernews: {
    name: 'Hacker News API',
    endpoint: 'https://hacker-news.firebaseio.com/v0',
    rateLimit: 1000,
    free: true,
    description: '技术新闻、AI话题讨论、创业趋势',
    documentation: 'https://github.com/HackerNews/API'
  },

  // =============================================================================
  // 免费但需要注册的数据源
  // =============================================================================
  newsapi: {
    name: 'NewsAPI',
    endpoint: 'https://newsapi.org/v2',
    apiKey: import.meta.env.VITE_NEWS_API_KEY,
    rateLimit: 1000, // 开发者计划：1000请求/天
    free: false,
    description: '全球新闻数据、媒体关注度、公司提及率',
    documentation: 'https://newsapi.org/docs'
  },

  semanticscholar: {
    name: 'Semantic Scholar API',
    endpoint: 'https://api.semanticscholar.org',
    apiKey: import.meta.env.VITE_SEMANTIC_SCHOLAR_KEY,
    rateLimit: 100,
    free: true,
    description: '学术论文语义分析、研究影响力、作者网络',
    documentation: 'https://api.semanticscholar.org/'
  },

  alphavantage: {
    name: 'Alpha Vantage',
    endpoint: 'https://www.alphavantage.co/query',
    apiKey: import.meta.env.VITE_ALPHA_VANTAGE_KEY,
    rateLimit: 5, // 免费：5/分钟
    free: true,
    description: 'AI公司股价、市场表现、投资趋势',
    documentation: 'https://www.alphavantage.co/documentation/'
  },

  // =============================================================================
  // 政府和组织开放数据
  // =============================================================================
  oecd: {
    name: 'OECD Statistics',
    endpoint: 'https://stats.oecd.org/SDMX-JSON',
    rateLimit: 60,
    free: true,
    description: '经合组织统计数据、数字经济指标、创新投入',
    documentation: 'https://data.oecd.org/api/'
  },

  eurostat: {
    name: 'Eurostat API',
    endpoint: 'https://ec.europa.eu/eurostat/api',
    rateLimit: 60,
    free: true,
    description: '欧盟统计数据、数字化程度、技术采用',
    documentation: 'https://ec.europa.eu/eurostat/web/json-and-unicode-web-services'
  },

  uspto: {
    name: 'USPTO Patent API',
    endpoint: 'https://ped.uspto.gov/api',
    rateLimit: 50,
    free: true,
    description: 'AI专利申请、技术创新、知识产权趋势',
    documentation: 'https://ped.uspto.gov/peds'
  },

  // =============================================================================
  // 付费高级数据源 (可选)
  // =============================================================================
  crunchbase: {
    name: 'Crunchbase API',
    endpoint: 'https://api.crunchbase.com/api/v4',
    apiKey: import.meta.env.VITE_CRUNCHBASE_KEY,
    rateLimit: 200,
    free: false,
    description: '创业公司数据、AI投资、并购信息',
    documentation: 'https://data.crunchbase.com/docs'
  },

  pitchbook: {
    name: 'PitchBook API',
    endpoint: 'https://api.pitchbook.com',
    apiKey: import.meta.env.VITE_PITCHBOOK_KEY,
    rateLimit: 100,
    free: false,
    description: '私募股权、风险投资、市场分析',
    documentation: 'https://pitchbook.com/platform/excel-plugin'
  }
};

// =============================================================================
// 数据采集优先级配置
// =============================================================================
export const COLLECTION_PRIORITY = {
  // 高优先级 - 实时更新
  high: [
    'github',
    'newsapi', 
    'hackernews',
    'reddit'
  ],
  
  // 中优先级 - 每日更新
  medium: [
    'arxiv',
    'semanticscholar',
    'alphavantage'
  ],
  
  // 低优先级 - 每周更新
  low: [
    'worldbank',
    'oecd',
    'eurostat',
    'uspto'
  ]
};

// =============================================================================
// API密钥获取指南
// =============================================================================
export const API_SETUP_GUIDE = {
  steps: [
    {
      title: '1. 免费即用API',
      apis: ['GitHub', 'arXiv', 'World Bank', 'Hacker News'],
      description: '这些API无需注册即可使用，但有请求频率限制'
    },
    {
      title: '2. 免费注册API',
      apis: ['News API', 'Alpha Vantage', 'Semantic Scholar'],
      description: '需要注册获取API密钥，但完全免费',
      action: '访问各自官网注册账号'
    },
    {
      title: '3. 高级付费API (可选)',
      apis: ['Crunchbase', 'PitchBook'],
      description: '提供更详细的商业数据，需要付费订阅',
      action: '评估预算后决定是否订阅'
    }
  ]
};

// =============================================================================
// 数据质量配置
// =============================================================================
export const DATA_QUALITY = {
  // 置信度权重
  confidenceWeights: {
    github: 0.25,      // 技术生态指标
    arxiv: 0.20,       // 学术研究指标  
    worldbank: 0.15,   // 政府数据指标
    newsapi: 0.15,     // 媒体关注指标
    semanticscholar: 0.10, // 研究影响力
    alphavantage: 0.10,    // 市场表现
    reddit: 0.05       // 社区讨论
  },
  
  // 数据验证规则
  validation: {
    minSources: 3,      // 至少需要3个数据源
    maxAge: 7,          // 数据最大年龄（天）
    outlierThreshold: 2 // 异常值标准差阈值
  }
};

// =============================================================================
// 缓存配置
// =============================================================================
export const CACHE_CONFIG = {
  ttl: {
    realtime: 5,    // 实时数据缓存5分钟
    daily: 1440,    // 日更新数据缓存24小时
    weekly: 10080,  // 周更新数据缓存7天
    static: 43200   // 静态数据缓存30天
  },
  
  maxSize: 100, // 最大缓存条目数
  cleanupInterval: 3600000 // 清理间隔：1小时
};

// =============================================================================
// 错误处理配置
// =============================================================================
export const ERROR_HANDLING = {
  retryAttempts: 3,
  retryDelay: 1000, // 1秒
  fallbackEnabled: true,
  
  // 降级策略
  degradationLevels: [
    'all_sources',      // 所有数据源正常
    'core_sources',     // 仅核心数据源
    'cached_data',      // 仅缓存数据
    'mock_data'         // 模拟数据
  ]
}; 